import {
  MensagemDtoSchema,
  TicketDetalheDtoSchema,
  TicketDtoSchema,
  TicketPageSchema,
  UnreadCountResponseSchema,
  type AdminTicketQuery,
  type CriarTicketInput,
  type MensagemDto,
  type SuporteCategoria,
  type SuporteTicketStatus,
  type TicketDetalheDto,
  type TicketDto,
  type TicketPage,
  type UnreadCountResponse,
  type UserTicketQuery,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 33 — Suporte Ticket System.
 *
 * User-facing:
 *   criarTicket(body)                        → POST /suporte/tickets
 *   listarTickets(query)                     → GET  /suporte/tickets?status=&cursor=&limit=
 *   detalheTicket(id)                        → GET  /suporte/tickets/:id
 *   responderTicket(ticketId, mensagem)      → POST /suporte/tickets/:id/mensagens
 *   unreadTicketCount()                      → GET  /suporte/tickets/unread-count
 *
 * Admin-facing:
 *   adminListarTickets(query)                → GET  /admin/suporte/tickets?status=&categoria=&cursor=&limit=
 *   adminDetalheTicket(id)                   → GET  /admin/suporte/tickets/:id
 *   adminResponderTicket(ticketId, mensagem) → POST /admin/suporte/tickets/:id/mensagens
 *   adminResolverTicket(ticketId)            → PUT  /admin/suporte/tickets/:id/resolver
 */
export class SuporteEndpoints {
  constructor(private readonly client: ApiClient) {}

  // ---- User-facing ----

  criarTicket(body: CriarTicketInput): Promise<TicketDto> {
    return this.client.post<TicketDto>('/suporte/tickets', body, {
      responseSchema: TicketDtoSchema,
    });
  }

  listarTickets(query: Partial<UserTicketQuery> = {}): Promise<TicketPage> {
    return this.client.get<TicketPage>('/suporte/tickets', {
      query: {
        ...(query.status ? { status: query.status as string } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
      },
      responseSchema: TicketPageSchema,
    });
  }

  detalheTicket(id: string): Promise<TicketDetalheDto> {
    return this.client.get<TicketDetalheDto>(`/suporte/tickets/${id}`, {
      responseSchema: TicketDetalheDtoSchema,
    });
  }

  responderTicket(ticketId: string, mensagem: string): Promise<MensagemDto> {
    return this.client.post<MensagemDto>(`/suporte/tickets/${ticketId}/mensagens`, { mensagem }, {
      responseSchema: MensagemDtoSchema,
    });
  }

  unreadTicketCount(): Promise<UnreadCountResponse> {
    return this.client.get<UnreadCountResponse>('/suporte/tickets/unread-count', {
      responseSchema: UnreadCountResponseSchema,
    });
  }

  // ---- Admin-facing ----

  adminListarTickets(query: Partial<AdminTicketQuery> = {}): Promise<TicketPage> {
    return this.client.get<TicketPage>('/admin/suporte/tickets', {
      query: {
        ...(query.status ? { status: query.status as string } : {}),
        ...(query.categoria ? { categoria: query.categoria as string } : {}),
        ...(query.cursor ? { cursor: query.cursor } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
      },
      responseSchema: TicketPageSchema,
    });
  }

  adminDetalheTicket(id: string): Promise<TicketDetalheDto> {
    return this.client.get<TicketDetalheDto>(`/admin/suporte/tickets/${id}`, {
      responseSchema: TicketDetalheDtoSchema,
    });
  }

  adminResponderTicket(ticketId: string, mensagem: string): Promise<MensagemDto> {
    return this.client.post<MensagemDto>(`/admin/suporte/tickets/${ticketId}/mensagens`, { mensagem }, {
      responseSchema: MensagemDtoSchema,
    });
  }

  adminResolverTicket(ticketId: string): Promise<TicketDto> {
    return this.client.put<TicketDto>(`/admin/suporte/tickets/${ticketId}/resolver`, undefined, {
      responseSchema: TicketDtoSchema,
    });
  }
}

export type {
  CriarTicketInput,
  TicketDto,
  TicketDetalheDto,
  MensagemDto,
  TicketPage,
  UnreadCountResponse,
  UserTicketQuery,
  AdminTicketQuery,
  SuporteTicketStatus,
  SuporteCategoria,
};
